/**
 * D10 — Inbound → Outbound campaign cross-link card.
 *
 * Rendered inside the supervisor's conversation detail panel for any
 * inbound conversation whose ``customer_profile_id`` has recent
 * outbound-call activity (last 72h by default). Helps the operator
 * stitch context: "this customer is calling in because we left them
 * a voicemail yesterday" / "we tried three times this week, last
 * disposition: no_answer".
 *
 * Data source: ``GET /api/identity/profiles/{id}/recent-outbound-calls``
 * (Phase 2 backend endpoint B8). Returns a list of recent
 * campaign_messages keyed off the resolved customer profile, scoped
 * to the caller's tenant. Empty list ⇒ card hides itself.
 */
import React, { useEffect, useState } from "react";
import { COLORS } from "../../theme/colors";
import { Badge } from "../../components/ui";
import { outboundVoiceApi } from "../../api/client";

const DISPOSITION_COLORS = {
  answered: COLORS.green,
  voicemail_left: COLORS.accent,
  no_answer: COLORS.textMuted,
  busy: COLORS.orange,
  failed: COLORS.pink,
  dnc: COLORS.pink,
  pending: COLORS.textMuted,
};

export default function RecentOutboundCallsCard({ profileId, lookbackHours = 72 }) {
  const [calls, setCalls] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!profileId) { setCalls(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await outboundVoiceApi.recentOutboundCalls(profileId, lookbackHours, 5);
        if (cancelled) return;
        setCalls(res?.data || res || []);
      } catch (e) {
        if (!cancelled) {
          // 404 / 412 (profile not found, attestation gate) → hide
          // silently. Network errors → keep card hidden too to
          // avoid noisy banners on every inbound chat.
          setError(e.message || String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [profileId, lookbackHours]);

  if (!profileId) return null;
  if (loading) return null;
  if (error || !calls || calls.length === 0) return null;

  return (
    <div style={{
      margin: "12px 20px",
      padding: 14,
      borderRadius: 10,
      background: `${COLORS.accent}08`,
      border: `1px solid ${COLORS.accent}30`,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <strong style={{ fontSize: 12, color: COLORS.text }}>
          Recent outbound calls (last {lookbackHours}h)
        </strong>
        <Badge color={COLORS.accent}>{calls.length}</Badge>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {calls.map((c, i) => (
          <div key={c.id || i} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "6px 8px", borderRadius: 6,
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            fontSize: 11,
          }}>
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
              <span style={{ color: COLORS.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c.campaign_name || "Voice campaign"}
              </span>
              <span style={{ color: COLORS.textMuted, fontSize: 10 }}>
                {c.attempted_at ? new Date(c.attempted_at).toLocaleString() : "—"}
                {c.duration_seconds ? `  · ${c.duration_seconds}s` : ""}
              </span>
            </div>
            <Badge color={DISPOSITION_COLORS[c.disposition] || COLORS.textMuted}>
              {c.disposition || "pending"}
            </Badge>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 8 }}>
        Customer is calling in after recent outbound activity — they may be following up. Context above for warm-handle.
      </div>
    </div>
  );
}
